// discussionService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs, 
  getDoc,
  onSnapshot, 
  arrayUnion, 
  serverTimestamp,
  query,
  orderBy,
  where
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { db, storage } from './firebaseConfig'; // Adjust path as needed

// Get all discussions for an application
export const getApplicationDiscussions = async (applicationId) => {
  try {
    const discussionsRef = collection(db, 'applications', applicationId, 'discussions');
    const q = query(discussionsRef, orderBy('timestamp', 'asc'));
    const snapshot = await getDocs(q);
    
    const discussions = [];
    snapshot.forEach((doc) => {
      discussions.push({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp // Ensure timestamp is included
      });
    });

    return {
      success: true,
      discussions: discussions
    };
  } catch (error) {
    console.error('Error fetching discussions:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Send a new message
export const sendDiscussionMessage = async (applicationId, messageData) => {
  try {
    const discussionsRef = collection(db, 'applications', applicationId, 'discussions');
    
    // Create message with proper structure
    const newMessage = {
      ...messageData,
      timestamp: new Date(),
      readBy: [messageData.sender],
      createdAt: new Date().toISOString(), // Fallback timestamp
    };

    // Add the message to Firestore
    const docRef = await addDoc(discussionsRef, newMessage);
    
    console.log('Message sent successfully:', docRef.id);
    
    return {
      success: true,
      messageId: docRef.id,
      message: newMessage
    };
  } catch (error) {
    console.error('Error sending message:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Upload document/file
export const uploadDiscussionDocument = async (applicationId, file, metadata = {}) => {
  try {
    const fileExtension = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExtension}`;
    const storageRef = ref(storage, `discussions/${applicationId}/${fileName}`);
    
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    const document = {
      id: fileName,
      fileName: file.name,
      originalName: file.name,
      url: downloadURL,
      size: file.size,
      type: file.type,
      uploadedAt: new Date(),
      ...metadata
    };

    return {
      success: true,
      document: document
    };
  } catch (error) {
    console.error('Error uploading document:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Mark messages as read
export const markMessagesAsRead = async (applicationId, userId) => {
  try {
    const discussionsRef = collection(db, 'applications', applicationId, 'discussions');
    const snapshot = await getDocs(discussionsRef);
    
    const updatePromises = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.readBy && !data.readBy.includes(userId)) {
        updatePromises.push(
          updateDoc(doc.ref, {
            readBy: arrayUnion(userId)
          })
        );
      }
    });

    await Promise.all(updatePromises);
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Error marking messages as read:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Submit Q&A answer
export const submitQuestionAnswer = async (applicationId, answerData) => {
  try {
    const { messageId, questionId, ...answer } = answerData;
    
    // Find the message document by ID (not by custom field)
    const messageDocRef = doc(db, 'applications', applicationId, 'discussions', messageId);
    const messageDoc = await getDoc(messageDocRef);
    
    if (!messageDoc.exists()) {
      throw new Error('Message not found');
    }

    const messageData = messageDoc.data();
    
    // Update the specific question with the new answer
    const updatedQuestions = messageData.qnaQuestions.map(question => {
      if (question.id === questionId) {
        const existingAnswers = question.answers || [];
        // Remove any existing answer from the same user
        const filteredAnswers = existingAnswers.filter(
          ans => ans.respondentId !== answer.respondentId
        );
        
        return {
          ...question,
          answers: [...filteredAnswers, {
            ...answer,
            id: `ans_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date(),
            createdAt: new Date().toISOString()
          }]
        };
      }
      return question;
    });

    await updateDoc(messageDocRef, {
      qnaQuestions: updatedQuestions,
      lastUpdated: new Date()
    });

    console.log('Answer submitted successfully');
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Error submitting answer:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Subscribe to real-time updates using onSnapshot
export const subscribeToApplicationUpdates = (applicationId, callback) => {
  try {
    const discussionsRef = collection(db, 'applications', applicationId, 'discussions');
    const q = query(discussionsRef, orderBy('timestamp', 'asc'));
    
    console.log('Setting up real-time listener for application:', applicationId);
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        console.log('Real-time update received, documents:', snapshot.size);
        
        const discussions = [];
        snapshot.docChanges().forEach((change) => {
          console.log('Document change:', change.type, change.doc.id);
        });
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          discussions.push({
            id: doc.id,
            ...data,
            // Handle Firestore timestamp conversion
            timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.createdAt || Date.now())
          });
        });
        
        console.log('Processed discussions:', discussions.length);
        console.log('Sample discussion with Q&A:', discussions.find(d => d.qnaQuestions?.length > 0));
        
        // Call the callback with the updated discussions
        callback({ 
          discussionMessages: discussions,
          lastUpdate: new Date()
        });
      }, 
      (error) => {
        console.error('Error in real-time listener:', error);
        // Try to reconnect or handle error gracefully
        callback({ 
          error: error.message,
          discussionMessages: [] 
        });
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up real-time listener:', error);
    return () => {}; // Return empty function if setup fails
  }
};

// Get user profile (mock function - replace with your actual implementation)
export const getUserProfile = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return {
        success: true,
        profile: userDoc.data()
      };
    } else {
      return {
        success: false,
        error: 'User not found'
      };
    }
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return {
      success: false,
      error: error.message
    };
  }
};